import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "热血排行", to: "/ranking" },
  { label: "爽漫推荐", to: "/recommend" },
  { label: "关于我们", to: "/about" },
  { label: "常见问题", to: "/faq" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/recommend?q=${encodeURIComponent(kw)}`);
    setKeyword("");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-300/60"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 md:h-18 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 text-background-50">
              <i className="ri-fire-fill text-xl"></i>
            </span>
            <span className="font-heading text-xl md:text-2xl tracking-wide">
              炮渣<span className="fire-text">漫画</span>网
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                  location.pathname === item.to
                    ? "text-primary-400 font-semibold"
                    : "text-foreground-600 hover:text-foreground-950"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center flex-1 max-w-xs">
            <div className="flex items-center w-full gap-2 rounded-full bg-background-100 border border-background-300/60 px-4 py-2 focus-within:border-primary-500/60">
              <i className="ri-search-line text-foreground-600 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜爽漫，如：炼丹、都市…"
                className="w-full bg-transparent text-sm text-foreground-950 placeholder:text-foreground-700 outline-none"
                aria-label="搜索漫画"
              />
            </div>
          </form>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <Link
              to="/recommend"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-flashlight-fill text-base"></i>马上开刷
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden flex h-10 w-10 items-center justify-center rounded-lg bg-background-100 border border-background-300/60 text-foreground-950"
            aria-label="打开菜单"
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden border-t border-background-300/60 bg-background-50/95 backdrop-blur-md px-4 py-4">
          <form onSubmit={handleSearch} className="flex items-center gap-2 mb-3">
            <div className="flex items-center flex-1 gap-2 rounded-full bg-background-100 border border-background-300/60 px-4 py-2">
              <i className="ri-search-line text-foreground-600 text-sm"></i>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜爽漫，如：炼丹、都市…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-foreground-700"
                aria-label="搜索漫画"
              />
            </div>
            <button
              type="submit"
              className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary-500 text-background-50"
              aria-label="搜索"
            >
              <i className="ri-search-line"></i>
            </button>
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-4 py-3 rounded-lg text-sm ${
                  location.pathname === item.to
                    ? "bg-background-100 text-primary-400 font-semibold"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}